import { isChatMessage } from './chat-messages'

// One lane per recipient/account. Failed entries are skipped, never retried
// automatically. Manual retry goes to the tail of its lane with the same UUID.
export function createChatSendQueue ({ authorId, isActive, post, changed, sent, report }) {
  const entries = []
  const running = new Set()
  let disposed = false
  let sequence = 0
  const active = () => !disposed && isActive()
  const notify = change => { if (active()) changed(change) }
  const laneKey = id => String(id)

  function confirm (messages) {
    if (!active()) return
    for (const message of messages) {
      if (!isChatMessage(message) || message.author_id != authorId) continue
      const entry = entries.find(e => e.message.client_message_id === message.client_message_id && e.message.user_id == message.user_id)
      if (entry) entry.message = { ...message }
    }
    notify()
    // Confirmed messages now belong to history/the visible list, not outbox.
    // Reopening the conversation must load the canonical server order.
    for (let i = entries.length - 1; i >= 0; i--) {
      if (entries[i].message.id != null) entries.splice(i, 1)
    }
    for (const entry of entries) drain(entry.message.user_id)
  }

  async function drain (userId) {
    const key = laneKey(userId)
    if (!active() || running.has(key)) return
    const lane = entries.filter(e => e.message.user_id == userId)
    const entry = lane.find(e => e.message.delivery_state === 'queued')
    if (!entry) return
    running.add(key)
    entry.message = { ...entry.message, delivery_state: 'sending' }
    notify()
    try {
      const response = await post({
        user_id: userId,
        client_message_id: entry.message.client_message_id,
        message: entry.message.message
      })
      if (!active()) return
      const data = response.data
      if (![200, 201].includes(response.status) || !isChatMessage(data) || data.client_message_id !== entry.message.client_message_id || data.author_id != authorId || data.user_id != userId) {
        const error = new Error('Invalid response contract: POST /api/messages')
        error.contract = { http_status: response.status, response_type: typeof data }
        throw error
      }
      confirm([data])
      if (entry.retried && response.status === 201) {
        notify({ reposition: { message: data, order: entry.order } })
      }
      // A realtime/UI error must not turn a successful save into a failed send.
      try { sent(data) } catch (error) { report(error) }
    } catch (error) {
      if (!active()) return
      report(error)
      // A GET/realtime event can confirm persistence before the POST settles.
      if (entry.message.id == null) {
        // Only statuses which reject this application's request before save
        // are definite failures. 5xx/timeouts may happen AFTER persistence.
        const status = error.response?.status
        const rejected = [400, 401, 403, 404, 409, 413, 419, 422, 429].includes(status)
        entry.message = { ...entry.message, delivery_state: rejected ? 'failed' : 'unconfirmed',
          retryable: ![400, 403, 404, 409, 413, 422].includes(status) }
        notify()
      }
    } finally {
      running.delete(key)
      if (active()) drain(userId)
    }
  }

  return {
    enqueue (message) {
      if (!active()) return
      const order = ++sequence
      entries.push({ message: { ...message, delivery_state: 'queued', delivery_order: order }, order })
      notify()
      drain(message.user_id)
    },
    messages (userId) {
      return entries.filter(e => e.message.user_id == userId).map(e => ({ ...e.message })).reverse()
    },
    retry (clientId, userId) {
      if (!active()) return
      const index = entries.findIndex(e => e.message.client_message_id === clientId && e.message.user_id == userId)
      const entry = entries[index]
      if (!entry || entry.message.id != null || !entry.message.retryable || !['failed', 'unconfirmed'].includes(entry.message.delivery_state)) return
      entries.splice(index, 1)
      entry.retried = true
      entry.order = ++sequence
      entry.message = { ...entry.message, delivery_state: 'queued', delivery_order: entry.order, retryable: false }
      entries.push(entry)
      notify()
      drain(userId)
    },
    hasUnsent () {
      return entries.some(e => e.message.id == null)
    },
    confirm,
    dispose () { disposed = true; entries.length = 0 }
  }
}
